import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Platform,
  Image,
} from 'react-native';
import { appointmentManager, Appointment } from '../utils/appointmentManager';
import BusinessOwnerBottomMenu from '../components/BusinessOwnerBottomMenu';
import MobileDataService from '../services/mobileDataService';

interface AnalyticsScreenProps {
  onBack: () => void;
  username?: string;
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'analytics') => void;
  onNavigateToSettings?: () => void;
  onLogout?: () => void;
}

const { width } = Dimensions.get('window');

const AnalyticsScreen: React.FC<AnalyticsScreenProps> = ({ onBack, username, onTabPress, onNavigateToSettings, onLogout }) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  // Initialize shared data service
  const dataService = MobileDataService.getInstance();

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      console.log('📊 Loading appointments for analytics from Firestore...');
      
      // Import AuthManager to get business owner ID
      const { AuthManager } = await import('../utils/authManager');
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      
      if (!businessOwner) {
        console.warn('⚠️  No business owner found, cannot load appointments');
        setAppointments([]);
        setLoading(false);
        return;
      }
      
      const businessId = businessOwner.id;
      console.log('📊 Loading appointments for business:', businessId);
      
      // Use appointmentManager which now loads from Firestore
      const allAppointments = await appointmentManager.getAppointments();
      
      // Filter appointments by business owner ID
      const filteredAppointments = allAppointments.filter(apt => {
        return (apt as any).businessOwnerId === businessId;
      });
      
      console.log(`📊 Loaded ${filteredAppointments.length} appointments from Firestore for analytics`);
      setAppointments(filteredAppointments);
      
      // Update analytics in Firestore (async, don't wait)
      updateAnalyticsInFirestore(businessId, filteredAppointments).catch(err => {
        console.error('Error updating analytics in Firestore:', err);
      });
      
    } catch (error) {
      console.error('Error loading appointments:', error);
      console.error('Error details:', error);
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Update analytics aggregates in Firestore
  const updateAnalyticsInFirestore = async (businessId: string, appointments: Appointment[]) => {
    try {
      if (Platform.OS !== 'web') return;
      
      const { doc, setDoc } = await import('firebase/firestore');
      const { getWebDb } = await import('../services/unifiedAuthService');
      const db = getWebDb();
      
      if (!db) return;
      
      // Calculate analytics
      const totalAppointments = appointments.length;
      const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed').length;
      const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
      const paidAppointments = appointments.filter(apt => apt.status === 'paid').length;
      const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled').length;
      
      const totalRevenue = appointments
        .filter(apt => apt.status === 'paid')
        .reduce((sum, apt) => sum + (apt.paymentData?.amount || 0), 0);
      
      const averageRevenue = paidAppointments > 0 ? totalRevenue / paidAppointments : 0;
      
      // Service type analytics
      const serviceTypes = appointments.reduce((acc, apt) => {
        acc[apt.serviceType] = (acc[apt.serviceType] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      // Time-based analytics (last 7 days)
      const now = new Date();
      const last7DaysAppointments = appointments.filter(apt => {
        const aptDate = new Date(apt.appointmentDate);
        const diffTime = now.getTime() - aptDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays <= 7;
      }).length;
      
      const thisWeekRevenue = appointments
        .filter(apt => {
          const aptDate = new Date(apt.appointmentDate);
          const diffTime = now.getTime() - aptDate.getTime();
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          return diffDays <= 7 && apt.status === 'paid';
        })
        .reduce((sum, apt) => sum + (apt.paymentData?.amount || 0), 0);
      
      // Worker performance
      const workerStats = appointments.reduce((acc, apt) => {
        if (apt.workerName) {
          if (!acc[apt.workerName]) {
            acc[apt.workerName] = { total: 0, completed: 0, totalDuration: 0 };
          }
          acc[apt.workerName].total++;
          if (apt.status === 'completed' || apt.status === 'paid') {
            acc[apt.workerName].completed++;
          }
          if (apt.actualDuration) {
            acc[apt.workerName].totalDuration += apt.actualDuration;
          }
        }
        return acc;
      }, {} as Record<string, { total: number; completed: number; totalDuration: number }>);
      
      // Store analytics in Firestore
      const analyticsData = {
        totalAppointments,
        confirmedAppointments,
        completedAppointments,
        paidAppointments,
        cancelledAppointments,
        totalRevenue,
        averageRevenue,
        serviceTypes,
        last7DaysAppointments,
        thisWeekRevenue,
        workerStats,
        updatedAt: new Date().toISOString(),
      };
      
      await setDoc(doc(db, 'businesses', businessId, 'analytics', 'current'), analyticsData);
      console.log('✅ Analytics updated in Firestore');
    } catch (error) {
      console.error('Error updating analytics in Firestore:', error);
    }
  };

  // Calculate analytics data
  const totalAppointments = appointments.length;
  const confirmedAppointments = appointments.filter(apt => apt.status === 'confirmed').length;
  const completedAppointments = appointments.filter(apt => apt.status === 'completed').length;
  const paidAppointments = appointments.filter(apt => apt.status === 'paid').length;
  const cancelledAppointments = appointments.filter(apt => apt.status === 'cancelled').length;

  // Revenue calculations
  const totalRevenue = appointments
    .filter(apt => apt.status === 'paid')
    .reduce((sum, apt) => sum + (apt.paymentData?.amount || 0), 0);

  const averageRevenue = paidAppointments > 0 ? totalRevenue / paidAppointments : 0;

  // Service type analytics
  const serviceTypes = appointments.reduce((acc, apt) => {
    acc[apt.serviceType] = (acc[apt.serviceType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topService = Object.entries(serviceTypes)
    .sort(([,a], [,b]) => b - a)[0];

  // Worker performance
  const workerStats = appointments.reduce((acc, apt) => {
    if (apt.workerName) {
      if (!acc[apt.workerName]) {
        acc[apt.workerName] = { total: 0, completed: 0, totalDuration: 0 };
      }
      acc[apt.workerName].total++;
      if (apt.status === 'completed' || apt.status === 'paid') {
        acc[apt.workerName].completed++;
      }
      if (apt.actualDuration) {
        acc[apt.workerName].totalDuration += apt.actualDuration;
      }
    }
    return acc;
  }, {} as Record<string, { total: number; completed: number; totalDuration: number }>);

  // Time-based analytics (last 7 days)
  const last7Days = appointments.filter(apt => {
    const aptDate = new Date(apt.appointmentDate);
    const now = new Date();
    const diffTime = now.getTime() - aptDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  }).length;

  const thisWeekRevenue = appointments
    .filter(apt => {
      const aptDate = new Date(apt.appointmentDate);
      const now = new Date();
      const diffTime = now.getTime() - aptDate.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return diffDays <= 7 && apt.status === 'paid';
    })
    .reduce((sum, apt) => sum + (apt.paymentData?.amount || 0), 0);

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>📈 Analytics</Text>
          <TouchableOpacity style={styles.backButton} onPress={onBack}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>📈 Analytics Dashboard</Text>
          <TouchableOpacity style={styles.backButton} onPress={onBack}>
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
        </View>

        {/* Overview Cards */}
        <View style={styles.overviewSection}>
          <Text style={styles.sectionTitle}>📊 Overview</Text>
          <View style={styles.cardsRow}>
            <View style={styles.overviewCard}>
              <Text style={styles.overviewNumber}>{totalAppointments}</Text>
              <Text style={styles.overviewLabel}>Total Appointments</Text>
            </View>
            <View style={styles.overviewCard}>
              <Text style={styles.overviewNumber}>{confirmedAppointments}</Text>
              <Text style={styles.overviewLabel}>Confirmed</Text>
            </View>
          </View>
          <View style={styles.cardsRow}>
            <View style={styles.overviewCard}>
              <Text style={styles.overviewNumber}>{completedAppointments}</Text>
              <Text style={styles.overviewLabel}>Completed</Text>
            </View>
            <View style={styles.overviewCard}>
              <Text style={styles.overviewNumber}>{paidAppointments}</Text>
              <Text style={styles.overviewLabel}>Paid</Text>
            </View>
          </View>
        </View>

        {/* Revenue Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>💰 Revenue Analytics</Text>
          <View style={styles.revenueCard}>
            <View style={styles.revenueRow}>
              <Text style={styles.revenueLabel}>Total Revenue</Text>
              <Text style={styles.revenueValue}>${totalRevenue.toFixed(2)}</Text>
            </View>
            <View style={styles.revenueRow}>
              <Text style={styles.revenueLabel}>Average per Appointment</Text>
              <Text style={styles.revenueValue}>${averageRevenue.toFixed(2)}</Text>
            </View>
            <View style={styles.revenueRow}>
              <Text style={styles.revenueLabel}>This Week's Revenue</Text>
              <Text style={styles.revenueValue}>${thisWeekRevenue.toFixed(2)}</Text>
            </View>
          </View>
        </View>

        {/* Service Analytics */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>🔧 Service Analytics</Text>
          <View style={styles.serviceCard}>
            <Text style={styles.serviceTitle}>Most Popular Service</Text>
            <Text style={styles.serviceName}>
              {topService ? topService[0] : 'No data'}
            </Text>
            <Text style={styles.serviceCount}>
              {topService ? `${topService[1]} appointments` : ''}
            </Text>
          </View>
          
          <View style={styles.serviceBreakdown}>
            <Text style={styles.breakdownTitle}>Service Breakdown</Text>
            {Object.entries(serviceTypes).map(([service, count]) => (
              <View key={service} style={styles.serviceItem}>
                <Text style={styles.serviceItemName}>{service}</Text>
                <Text style={styles.serviceItemCount}>{count}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Worker Performance */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>👥 Worker Performance</Text>
          {Object.entries(workerStats).map(([workerName, stats]) => (
            <View key={workerName} style={styles.workerCard}>
              <Text style={styles.workerName}>{workerName}</Text>
              <View style={styles.workerStats}>
                <View style={styles.workerStat}>
                  <Text style={styles.workerStatNumber}>{stats.total}</Text>
                  <Text style={styles.workerStatLabel}>Total</Text>
                </View>
                <View style={styles.workerStat}>
                  <Text style={styles.workerStatNumber}>{stats.completed}</Text>
                  <Text style={styles.workerStatLabel}>Completed</Text>
                </View>
                <View style={styles.workerStat}>
                  <Text style={styles.workerStatNumber}>
                    {stats.total > 0 ? Math.round(stats.totalDuration / stats.total) : 0}m
                  </Text>
                  <Text style={styles.workerStatLabel}>Avg Time</Text>
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* Recent Activity */}
        <View style={styles.section}>
          <View style={styles.sectionTitleRow}>
            <Image source={require('../assets/images/bookapt-logo.png')} style={styles.sectionTitleIcon} resizeMode="contain" />
            <Text style={styles.sectionTitle}>Recent Activity</Text>
          </View>
          <View style={styles.activityCard}>
            <Text style={styles.activityText}>
              {last7Days} appointments in the last 7 days
            </Text>
            <Text style={styles.activityText}>
              {thisWeekRevenue > 0 ? `$${thisWeekRevenue.toFixed(2)} revenue this week` : 'No revenue this week'}
            </Text>
          </View>
        </View>

        {/* Status Distribution */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>📊 Status Distribution</Text>
          <View style={styles.statusCard}>
            <View style={styles.statusItem}>
              <View style={[styles.statusDot, { backgroundColor: '#28a745' }]} />
              <Text style={styles.statusText}>Confirmed: {confirmedAppointments}</Text>
            </View>
            <View style={styles.statusItem}>
              <View style={[styles.statusDot, { backgroundColor: '#007bff' }]} />
              <Text style={styles.statusText}>Completed: {completedAppointments}</Text>
            </View>
            <View style={styles.statusItem}>
              <View style={[styles.statusDot, { backgroundColor: '#6f42c1' }]} />
              <Text style={styles.statusText}>Paid: {paidAppointments}</Text>
            </View>
            <View style={styles.statusItem}>
              <View style={[styles.statusDot, { backgroundColor: '#dc3545' }]} />
              <Text style={styles.statusText}>Cancelled: {cancelledAppointments}</Text>
            </View>
          </View>
        </View>
      </ScrollView>
      
      {/* Bottom Menu */}
      {onTabPress && (
        <BusinessOwnerBottomMenu
          activeTab="analytics"
          onTabPress={onTabPress}
          onNavigateToSettings={onNavigateToSettings}
          onLogout={onLogout}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100, // Space for bottom menu
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 5,
  },
  backButtonText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#6c757d',
  },
  overviewSection: {
    padding: 20,
  },
  section: {
    padding: 20,
    paddingTop: 0,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  sectionTitleIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  cardsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  overviewCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  overviewNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#007bff',
    marginBottom: 5,
  },
  overviewLabel: {
    fontSize: 12,
    color: '#6c757d',
    textAlign: 'center',
  },
  revenueCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  revenueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f8f9fa',
  },
  revenueLabel: {
    fontSize: 14,
    color: '#6c757d',
  },
  revenueValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#28a745',
  },
  serviceCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  serviceTitle: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 5,
  },
  serviceName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 5,
  },
  serviceCount: {
    fontSize: 14,
    color: '#007bff',
  },
  serviceBreakdown: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  breakdownTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 15,
  },
  serviceItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f8f9fa',
  },
  serviceItemName: {
    fontSize: 14,
    color: '#1C1C1E',
    flex: 1,
  },
  serviceItemCount: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#007bff',
  },
  workerCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  workerName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 10,
  },
  workerStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  workerStat: {
    alignItems: 'center',
  },
  workerStatNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#007bff',
  },
  workerStatLabel: {
    fontSize: 12,
    color: '#6c757d',
    marginTop: 2,
  },
  activityCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  activityText: {
    fontSize: 14,
    color: '#1C1C1E',
    marginBottom: 5,
  },
  statusCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  statusDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 10,
  },
  statusText: {
    fontSize: 14,
    color: '#1C1C1E',
  },
});

export default AnalyticsScreen;
